import React, { forwardRef } from "react";
import { ReactComponent as FincaIcon } from '../assets/icons/FincaIcon.svg';
import { ReactComponent as QR } from '../assets/icons/QR.svg';

const obtenerFechaActual = () => {
    const hoy = new Date();
    const dia = String(hoy.getDate()).padStart(2, '0');
    const mes = String(hoy.getMonth() + 1).padStart(2, '0');
    const anio = hoy.getFullYear();
    return `${dia}/${mes}/${anio}`;
};

// Símbolo real de la venta según su tipoDivisa
const SIMBOLOS_DIVISA = { Pesos: "$", "Dólares": "US$", Reales: "R$", Euros: "€" };

const Remito = forwardRef(({ datosCliente, productosVendidos, totalFinal, numeroComprobante, modalidadPago, tipoDivisa = 'Pesos' }, ref) => {
    const simboloDivisa = SIMBOLOS_DIVISA[tipoDivisa] || "$";
    
    const datosVendedor = {
        razonSocial: "Finca Los Astros S.A.S",
        domicilio: "Servidumbre de Tránsito s/n Fracción C 0, Piso C, El Peral, 5561, Provincia de Mendoza, Argentina",
        telefono: "+5491130609103",
        condicionIVA: "Responsable Inscripto",
        cuit: "30-71934761-0",
        ingresosBrutos: "1038057",
        fechaInicioActividades: "22/12/2025",
        puntoDeVenta: "00001",
        compNro: "00000001",
        fechaEmision: obtenerFechaActual()
    };

    const tipoComprobante = 'R';
    const codComprobante = 'COD.91';
    
    const getCondicionIVA = () => {
        return 'Consumidor final';
    };

    const tdStyle = {
        textAlign: 'center',
        padding: '4px',
        margin: '0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    };
    
    const tdStyleRight = {
        textAlign: 'right',
        padding: '4px',
        paddingRight: '10px',
        margin: '0',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap'
    };
    
    const thStyle = {
        textAlign: 'center',
        padding: '4px',
        fontWeight: 'bold'
    };

    const formatNumber = (number) => {
        return Number(number)?.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const renderProductos = () => {
        const getUnidadAbreviada = (unidad) => {
            switch (unidad) {
                case 'Hectolitros': return 'hl';
                case 'Quintales': return 'q';
                default: return 'u';
            }
        };

        return productosVendidos.map((item, index) => {
            // LECTURA DEL DESCUENTO
            const descuentoReal = Number(item?.descuento) || 0;
            const factorDescuento = (100 - descuentoReal) / 100;
            
            // CÁLCULOS
            const precioBaseTotalBruto = item?.cantidad * item?.precioUnidad;
            const precioBase = precioBaseTotalBruto * factorDescuento;
            
            const totalProducto = Math.floor(precioBase);
            const unidadAbreviada = getUnidadAbreviada(item?.tipoUnidad);
            const porcentajeDescuento = `${descuentoReal}%`;
            
            return (
                <tr key={index}>
                    <td style={{ textAlign: 'left', maxWidth: '110px' }}>{item?.nombreProducto}</td>
                    <td style={tdStyle}>{item?.cantidad}</td>
                    <td style={tdStyle}>{unidadAbreviada}</td>
                    <td style={tdStyleRight}>{simboloDivisa}{formatNumber(item?.precioUnidad)}</td>
                    <td style={tdStyle}>{porcentajeDescuento}</td>
                    <td style={tdStyleRight}>{simboloDivisa}{formatNumber(totalProducto)}</td>
                </tr>
            );
        });
    };

    // Actualizado para aplicar el descuento al cálculo total del remito
    const totalSinImpuestos = Math.floor(productosVendidos.reduce((total, item) => {
        const descuentoReal = Number(item?.descuento) || 0;
        const factorDescuento = (100 - descuentoReal) / 100;
        return total + Math.floor((item.cantidad * item.precioUnidad) * factorDescuento);
    }, 0));

    return (
        <div
            ref={ref}
            style={{
                minWidth: "210mm",
                minHeight: "297mm",
                padding: "17mm 10mm 10mm",
                background: "#fff",
                fontFamily: "Arial, sans-serif",
                color: "#333",
                margin: "0 auto",
                border: "1px solid #ddd",
                borderRadius: "4px",
                display: 'flex',
                flexDirection: 'column',
                boxSizing: "border-box",
                position: 'relative',
            }}
        >
            <div style={{ display: "flex", alignItems: "center" }}>
                <FincaIcon style={{ width: "32%", overflow: 'visible' }} />
            </div>
            <div style={{ position: 'absolute', top: '.75cm', right: '46%', textAlign: 'center', alignItems: 'center', justifyContent: 'center', gap: '18px', display: 'flex', flexDirection: 'column' }}>
                <span style={{ fontFamily: 'Courier New', fontWeight: 'normal', textAlign: 'center', margin: ' auto' }}>ORIGINAL</span>
                <div style={{ backgroundColor: 'white', border: '1px solid #808080', justifyContent: 'space-between', alignContent: 'center', width: '1.75cm', height: '1.75cm', padding: '5px', borderRadius: '10px', display: 'flex', flexDirection: 'column' }}>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontWeight: 'normal', textAlign: 'center', fontSize: '35px', margin: 'auto' }}>{tipoComprobante}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontWeight: 'normal', textAlign: 'center', fontSize: '12px', margin: 'auto' }}>{codComprobante}</p>
                </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: '50px', width: '100%' }}>
                <div style={{ width: 'fit-content' }}>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Razón social</b><br />{datosVendedor.razonSocial}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>
                        <b>Domicilio Comercial</b><br />
                        {datosVendedor.domicilio.split(', ').map((line, i) =>
                            <span key={i} style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>{line}<br /></span>
                        )}
                    </p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Teléfono de contacto</b><br />{datosVendedor.telefono}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Condición frente al IVA</b><br />{datosVendedor.condicionIVA}</p>
                </div>
                <div style={{ width: 'fit-content' }}>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>REMITO</b></p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>Punto de venta: {datosVendedor.puntoDeVenta}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>Comp.Nro.: {numeroComprobante || datosVendedor.compNro}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>Fecha de emisión: {datosVendedor.fechaEmision}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>CUIT: {datosVendedor.cuit}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>Ingresos Brutos: {datosVendedor.ingresosBrutos}</p>
                    <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}>Fecha de Inicio de Actividades: {datosVendedor.fechaInicioActividades}</p>
                </div>
            </div>
            <div style={{ borderTop: '1px solid #808080', borderBottom: '1px solid #808080', opacity: '0.5', height: '1mm', margin: '10px 0' }}></div>
            {datosCliente && (
                <div style={{ display: "flex", justifyContent: "space-between", width: '100%' }}>
                    <div style={{ width: 'fit-content' }}>
                        {datosCliente?.personeria === 'persona fisica' ? (
                            <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>{datosCliente?.tipo}</b><br />{datosCliente?.numeroDeDocumento}</p>
                        ) : (
                            <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>CUIL / CUIT</b><br />{datosCliente?.cuit}</p>
                        )}
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Condición frente al IVA</b><br />{getCondicionIVA()}</p>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Condición de venta</b><br />{modalidadPago}</p>
                    </div>
                    <div style={{ width: 'fit-content' }}>
                        {datosCliente?.personeria === 'persona fisica' ? (
                            <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Apellido y Nombre / Razón Social</b><br />{datosCliente?.apellido}, {datosCliente?.nombre}</p>
                        ) : (
                            <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Apellido y Nombre / Razón Social</b><br />{datosCliente?.razonSocial}</p>
                        )}
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Domicilio</b><br />
                            {datosCliente?.calle} {datosCliente?.altura}, {datosCliente?.piso} {datosCliente?.unidad}<br />
                            {datosCliente?.codigoPostal}<br />
                            {datosCliente?.ciudad}, {datosCliente?.pais}
                        </p>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '11px' }}><b>Teléfono de contacto</b><br />{datosCliente?.telefono}</p>
                    </div>
                </div>
            )}
            <div style={{ borderTop: '1px solid #808080', borderBottom: '1px solid #808080', opacity: '0.5', height: '1mm', margin: '10px 0' }}></div>
            <table
                style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontFamily: "'Courier New'",
                    fontSize: "11px",
                }}
            >
                <thead>
                    <tr>
                        <th style={thStyle}>Descripción</th>
                        <th style={thStyle}>Cantidad</th>
                        <th style={thStyle}>Unidad</th>
                        <th style={{ ...thStyle, minWidth: '75px' }}>Precio Uni.</th>
                        <th style={thStyle}>%DESC</th>
                        <th style={thStyle}>Total</th>
                    </tr>
                </thead>
                <tbody>
                    {renderProductos()}
                </tbody>
            </table>
            <div style={{
                margin: 'auto auto 0',
                display: 'flex',
                flexDirection: 'column',
                width: '100%',
                justifyContent: 'space-between',
                fontFamily: "'Courier New'",
                gap: '10px'
            }}>
                <div style={{
                    borderTop: '1px solid #cccccc',
                    padding: '10px 0',
                    borderBottom: '1px solid #cccccc', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', width: '100%'
                }}>
                    <div style={{ textAlign: 'right', fontSize: '14px', height: '100%', margin: '0 0 auto auto ' }}>
                        <tbody>
                            <tr>
                                <td style={{ ...tdStyle, textAlign: 'left', margin: '0', fontWeight: 'bold' }}>Importe total</td>
                                <td style={{ ...tdStyle, textAlign: 'right', margin: '0' }}>{simboloDivisa}{formatNumber(totalSinImpuestos)}</td>
                            </tr>
                        </tbody>
                    </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'row', gap: '10px' }}>
                    <QR style={{ height: '1.5cm', overflow: 'visible' }} />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '10px', fontWeight: 'bold' }}>CAE Nº:12345678912345</p>
                        <p style={{ fontFamily: 'Courier New', margin: '0', fontSize: '10px', fontWeight: 'bold' }}>Fecha de vencimiento de CAE:05/11/2028</p>
                    </div>
                </div>
            </div>
        </div>
    );
});

export default Remito;